import { ARCHITECTURE_SIGNAL } from "contracts/enums/issue-semantics";
import { METRIC_KEY } from "contracts/report/catalog";
import { contentFitnessSubcriterionForKey } from "contracts/report/content-fitness/lookups";
import type { MapChangeType } from "../../enums/map";
import {
  MAP_CHANGE_TYPE,
  MAP_EVIDENCE_KIND,
  MAP_EVIDENCE_TOPIC,
} from "../../enums/map";
import { architectureFact } from "../scan/issueSemantics";
import { journeyIssueTitle } from "../scan/journeyFriction";
import type { MapEvidence, MapPageFact } from "./mapTypes";
import { uniqueById } from "./mapUtilities";

const CREATE_KEYS = new Set([
  "content_design_orientation.internal_linking_depth",
  "content_design_orientation.url_design",
  "journey_usability_action.intent_journey_stage_fit",
  "journey_usability_action.navigation_link_quality",
]);
const CLARIFY_KEYS = new Set([
  "journey_usability_action.intent_journey_stage_fit",
  "journey_usability_action.navigation_link_quality",
  "journey_usability_action.action_path_friction_proof",
]);
const PATTERN_KEYS = new Set([
  "content_design_orientation.heading_hierarchy",
  "content_design_orientation.internal_linking_depth",
  "content_design_orientation.semantic_structure_markup",
  "content_design_orientation.content_order_chunking",
  "content_design_orientation.url_design",
  "journey_usability_action.intent_journey_stage_fit",
  "journey_usability_action.scannability_formatting",
  "journey_usability_action.navigation_link_quality",
  "journey_usability_action.action_path_friction_proof",
]);

export function relevantMapIssues(type: MapChangeType, facts: MapPageFact[]) {
  return uniqueById(facts.flatMap((fact) => fact.issues)).filter((issue) => {
    if (issue.originResourceKind !== null) return false;
    const key = issue.primarySubcriterionKey ?? "";
    const parent = issue.primarySubcriterionKey
      ? contentFitnessSubcriterionForKey(issue.primarySubcriterionKey)
          ?.parentMetricKey
      : issue.metricKey;
    if (
      parent !== METRIC_KEY.CONTENT_DESIGN_ORIENTATION &&
      parent !== METRIC_KEY.JOURNEY_USABILITY_ACTION
    ) {
      return false;
    }
    const architecture = architectureFact(issue);
    if (
      !architecture ||
      architecture.signals.includes(ARCHITECTURE_SIGNAL.NON_ARCHITECTURAL)
    ) {
      return false;
    }
    if (type === MAP_CHANGE_TYPE.CREATE_HUB) {
      return (
        CREATE_KEYS.has(key) &&
        architecture.signals.includes(ARCHITECTURE_SIGNAL.ORGANIZING)
      );
    }
    if (type === MAP_CHANGE_TYPE.CLARIFY_DESTINATION) {
      return (
        CLARIFY_KEYS.has(key) &&
        architecture.signals.includes(
          ARCHITECTURE_SIGNAL.DESTINATION_RELATIONSHIP,
        )
      );
    }
    if (type === MAP_CHANGE_TYPE.CONSOLIDATE_PAGES) {
      return architecture.signals.includes(ARCHITECTURE_SIGNAL.OVERLAP);
    }
    if (type === MAP_CHANGE_TYPE.IMPROVE_PATTERN) {
      return (
        PATTERN_KEYS.has(key) &&
        architecture.signals.includes(ARCHITECTURE_SIGNAL.PATTERN)
      );
    }
    if (type === MAP_CHANGE_TYPE.REORGANISE_GROUP) {
      return (
        (CREATE_KEYS.has(key) || PATTERN_KEYS.has(key)) &&
        architecture.signals.includes(ARCHITECTURE_SIGNAL.REORGANIZE)
      );
    }
    return false;
  });
}

export function issueEvidence(
  issues: ReturnType<typeof relevantMapIssues>,
  representedPageIds: Set<string>,
): MapEvidence[] {
  return issues.slice(0, 2).map((issue) => {
    const pageCount = new Set(
      issue.pages
        .filter((page) => representedPageIds.has(page.pageId))
        .map((page) => page.pageId),
    ).size;
    const displayedPageCount = pageCount || 1;
    return {
      kind: "issue" as const,
      topic: MAP_EVIDENCE_TOPIC.ISSUE_FINDING,
      heading: journeyIssueTitle(issue),
      detail: `${severityLabel(issue.severity)} finding linked to ${displayedPageCount} represented page${displayedPageCount === 1 ? "" : "s"}.`,
      issueId: issue.id,
    };
  });
}

export function coverageEvidence(facts: MapPageFact[]): MapEvidence {
  const assessed = facts.filter(
    (fact) => fact.page.contentFitness !== null,
  ).length;
  return {
    kind: MAP_EVIDENCE_KIND.COVERAGE,
    topic: MAP_EVIDENCE_TOPIC.ASSESSMENT_COVERAGE,
    detail: `${assessed} of ${facts.length} represented page${facts.length === 1 ? "" : "s"} ${facts.length === 1 ? "has" : "have"} Content Fitness assessment data.`,
  };
}

function severityLabel(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}
